import React, { useEffect, useMemo, useState } from 'react';
import { Container, Box, Button } from '@material-ui/core';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'react-simple-snackbar';
import styles from '../Clients/Clients.module.css';
import AddLocation from './AddLocation';
import AddContact from '../Contacts/AddContact';
import { getLocationsByUser, deleteLocation } from '../../actions/locationActions';
import { getAccountsByUser } from '../../actions/accountActions';
import { getContactsByUser, deleteContact } from '../../actions/contactActions';
import useAuthUser from '../../hooks/useAuthUser';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';

const Locations = () => {
  const auth = useAuthUser()
  const dispatch = useDispatch()
  const { locations, isLoading } = useSelector((state) => state.locations)
  const accounts = useSelector((state) => state.accounts.accounts || [])
  const contacts = useSelector((state) => state.contacts.contacts || [])
  const [locationModalOpen, setLocationModalOpen] = useState(false)
  const [currentId, setCurrentId] = useState(null)
  const [contactsPanelOpen, setContactsPanelOpen] = useState(false)
  const [contactsLocation, setContactsLocation] = useState(null)
  const [contactCurrentId, setContactCurrentId] = useState(null)
  const [contactFormOpen, setContactFormOpen] = useState(false)
  // eslint-disable-next-line no-unused-vars
  const [openSnackbar, closeSnackbar] = useSnackbar()

  useEffect(() => {
    const userId = auth?.result?._id || auth?.result?.googleId
    if (userId) {
      dispatch(getLocationsByUser({ search: userId }))
      if (!accounts.length) {
        dispatch(getAccountsByUser({ search: userId }))
      }
      dispatch(getContactsByUser({ search: userId }))
    }
  }, [auth, dispatch])

  const accountNameById = useMemo(() => {
    const map = {}
    accounts.forEach(a => { map[a._id] = a.name })
    return map
  }, [accounts])

  const rows = useMemo(() => {
    return [...(locations || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
  }, [locations])

  const handleEdit = (id) => {
    setCurrentId(id)
    setLocationModalOpen(true)
  }

  const handleDelete = (id) => {
    dispatch(deleteLocation(id, openSnackbar))
  }

  const handleOpenContacts = (location) => {
    setContactsLocation(location)
    setContactsPanelOpen(true)
    setContactCurrentId(null)
  }

  const handleDeleteContact = (id) => {
    dispatch(deleteContact(id, openSnackbar))
  }

  const columns = [
    { field: 'name', headerName: 'Name', flex: 1.2, minWidth: 180 },
    {
      field: 'accountId',
      headerName: 'Parent Account',
      flex: 1,
      minWidth: 160,
      valueGetter: (params) => accountNameById[params.value] || '',
    },
    { field: 'shippingAddress', headerName: 'Shipping Address', flex: 1.2, minWidth: 180 },
    { field: 'shippingCity', headerName: 'City', flex: 0.8, minWidth: 130 },
    { field: 'shippingState', headerName: 'State', flex: 0.4, minWidth: 90 },
    { field: 'shippingZip', headerName: 'Zip', flex: 0.5, minWidth: 100 },
    { field: 'phone', headerName: 'Phone', flex: 0.9, minWidth: 140 },
    {
      field: 'active',
      headerName: 'Active',
      flex: 0.4,
      minWidth: 90,
      renderCell: (params) => (
        <span style={{ color: params.value ? 'green' : 'gray', fontWeight: 600 }}>
          {params.value ? 'Yes' : 'No'}
        </span>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      width: 260,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Button
            style={{color: 'white', backgroundColor: 'rgb(25, 118, 210)', marginRight: 10}}
            onClick={(e) => { e.stopPropagation(); handleEdit(params.row._id); }}
            variant="contained"
            size="small"
          >
            Edit
          </Button>
          <Button
            style={{color: 'white', backgroundColor: 'red'}}
            onClick={(e) => { e.stopPropagation(); handleDelete(params.row._id); }}
            variant="contained"
            size="small"
          >
            Delete
          </Button>
          <Button
            style={{color: 'white', backgroundColor: '#555', marginLeft: 4}}
            onClick={(e) => { e.stopPropagation(); handleOpenContacts(params.row); }}
            variant="contained"
            size="small"
          >
            Contacts
          </Button>
        </Box>
      ),
    },
  ]

  return (
    <div className={styles.pageLayout}>
      <Container style={{ width: '90%', maxWidth: 1400 }}>
        <div className={styles.buttons}>
          <h1>Locations</h1>
          <button
            className={styles.btn}
            onClick={() => { setCurrentId(null); setLocationModalOpen((prev) => !prev) }}
          >
            Add Location
          </button>
        </div>
        <DataGrid
          autoHeight
          loading={isLoading}
          rows={rows}
          getRowId={(row) => row._id}
          columns={columns}
          pageSize={25}
          rowsPerPageOptions={[10, 25, 50, 100]}
          components={{ Toolbar: GridToolbar }}
          componentsProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 300 } } }}
          sx={{
            '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '6px' },
            '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '8px' },
            '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '12px' },
          }}
          onRowDoubleClick={(params) => handleEdit(params.id)}
        />
      </Container>
      <AddLocation open={locationModalOpen} setOpen={setLocationModalOpen} currentId={currentId} setCurrentId={setCurrentId} />
      <Dialog open={contactsPanelOpen} onClose={() => { setContactsPanelOpen(false); setContactCurrentId(null); setContactsLocation(null); setContactFormOpen(false) }} fullWidth maxWidth="sm">
        <DialogTitle>
          {contactsLocation ? `Contacts for ${contactsLocation.name || 'Location'}` : 'Contacts'}
        </DialogTitle>
        <DialogContent dividers>
          {(contacts.filter(c => c.locationId === contactsLocation?._id)).length === 0 ? (
            <p style={{ color: '#555' }}>No contacts for this location.</p>
          ) : (
            (contacts.filter(c => c.locationId === contactsLocation?._id)).map((c) => (
              <Box key={c._id} display="flex" alignItems="center" justifyContent="space-between" style={{ padding: '6px 0' }}>
                <div>
                  <strong>{c.name}</strong>{c.primary ? ' • Primary' : ''}<br />
                  <span style={{ color: '#555' }}>{c.role || ''}</span><br />
                  <span style={{ color: '#555' }}>{c.email || ''} {c.phone ? `• ${c.phone}` : ''}</span>
                </div>
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  <Button
                    style={{color: 'white', backgroundColor: 'rgb(25, 118, 210)'}}
                    variant="contained"
                    size="small"
                    onClick={() => { setContactCurrentId(c._id); setContactFormOpen(true); }}
                  >
                    Edit
                  </Button>
                  <Button
                    style={{color: 'white', backgroundColor: 'red'}}
                    variant="contained"
                    size="small"
                    onClick={() => handleDeleteContact(c._id)}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setContactCurrentId(null); setContactFormOpen(true); }} color="primary" variant="contained">
            Add Contact
          </Button>
          <Button onClick={() => { setContactsPanelOpen(false); setContactCurrentId(null); setContactsLocation(null); setContactFormOpen(false) }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <AddContact
        open={contactFormOpen}
        setOpen={(val) => {
          if (!val) {
            setContactCurrentId(null)
            setContactFormOpen(false)
          } else {
            setContactFormOpen(true)
          }
        }}
        currentId={contactCurrentId}
        setCurrentId={setContactCurrentId}
        defaultAccountId={contactsLocation?.accountId}
        defaultLocationId={contactsLocation?._id}
      />
    </div>
  );
};

export default Locations;
